import _classCallCheck from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/createClass.js";
import firebase from '@firebase/app-compat';
import { _getChild, uploadBytesResumable, _dataFromString, _UploadTask, _FbsBlob, StringFormat, listAll as _listAll, list as _list, getMetadata as _getMetadata, updateMetadata as _updateMetadata, getDownloadURL as _getDownloadURL, deleteObject, _invalidRootOperation, _invalidArgument, ref as _ref2, _Location, connectStorageEmulator, _TaskState, _TaskEvent } from '@firebase/storage';
import { Component } from '@firebase/component';

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var UploadTaskSnapshotCompat = /*#__PURE__*/function () {
  function UploadTaskSnapshotCompat(_delegate, task, ref) {
    _classCallCheck(this, UploadTaskSnapshotCompat);
    this._delegate = _delegate;
    this.task = task;
    this.ref = ref;
  }
  _createClass(UploadTaskSnapshotCompat, [{
    key: "bytesTransferred",
    get: function get() {
      return this._delegate.bytesTransferred;
    }
  }, {
    key: "metadata",
    get: function get() {
      return this._delegate.metadata;
    }
  }, {
    key: "state",
    get: function get() {
      return this._delegate.state;
    }
  }, {
    key: "totalBytes",
    get: function get() {
      return this._delegate.totalBytes;
    }
  }]);
  return UploadTaskSnapshotCompat;
}();
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var UploadTaskCompat = /*#__PURE__*/function () {
  function UploadTaskCompat(_delegate, _ref) {
    _classCallCheck(this, UploadTaskCompat);
    this._delegate = _delegate;
    this._ref = _ref;
    this.cancel = this._delegate.cancel.bind(this._delegate);
    this.catch = this._delegate.catch.bind(this._delegate);
    this.pause = this._delegate.pause.bind(this._delegate);
    this.resume = this._delegate.resume.bind(this._delegate);
  }
  _createClass(UploadTaskCompat, [{
    key: "snapshot",
    get: function get() {
      return new UploadTaskSnapshotCompat(this._delegate.snapshot, this, this._ref);
    }
  }, {
    key: "then",
    value: function then(onFulfilled, onRejected) {
      var _this = this;
      return this._delegate.then(function (snapshot) {
        if (onFulfilled) {
          return onFulfilled(new UploadTaskSnapshotCompat(snapshot, _this, _this._ref));
        }
      }, onRejected);
    }
  }, {
    key: "on",
    value: function on(type, nextOrObserver, error, completed) {
      var _this2 = this;
      var wrappedNextOrObserver = undefined;
      if (!!nextOrObserver) {
        if (typeof nextOrObserver === 'function') {
          wrappedNextOrObserver = function wrappedNextOrObserver(taskSnapshot) {
            return nextOrObserver(new UploadTaskSnapshotCompat(taskSnapshot, _this2, _this2._ref));
          };
        } else {
          wrappedNextOrObserver = {
            next: !!nextOrObserver.next ? function (taskSnapshot) {
              return nextOrObserver.next(new UploadTaskSnapshotCompat(taskSnapshot, _this2, _this2._ref));
            } : undefined,
            complete: nextOrObserver.complete || undefined,
            error: nextOrObserver.error || undefined
          };
        }
      }
      return this._delegate.on(type, wrappedNextOrObserver, error || undefined, completed || undefined);
    }
  }]);
  return UploadTaskCompat;
}();
var ListResultCompat = /*#__PURE__*/function () {
  function ListResultCompat(_delegate, _service) {
    _classCallCheck(this, ListResultCompat);
    this._delegate = _delegate;
    this._service = _service;
  }
  _createClass(ListResultCompat, [{
    key: "prefixes",
    get: function get() {
      var _this3 = this;
      return this._delegate.prefixes.map(function (ref) {
        return new ReferenceCompat(ref, _this3._service);
      });
    }
  }, {
    key: "items",
    get: function get() {
      var _this4 = this;
      return this._delegate.items.map(function (ref) {
        return new ReferenceCompat(ref, _this4._service);
      });
    }
  }, {
    key: "nextPageToken",
    get: function get() {
      return this._delegate.nextPageToken || null;
    }
  }]);
  return ListResultCompat;
}();
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *  http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var ReferenceCompat = /*#__PURE__*/function () {
  function ReferenceCompat(_delegate, storage) {
    _classCallCheck(this, ReferenceCompat);
    this._delegate = _delegate;
    this.storage = storage;
  }
  _createClass(ReferenceCompat, [{
    key: "name",
    get: function get() {
      return this._delegate.name;
    }
  }, {
    key: "bucket",
    get: function get() {
      return this._delegate.bucket;
    }
  }, {
    key: "fullPath",
    get: function get() {
      return this._delegate.fullPath;
    }
  }, {
    key: "toString",
    value: function toString() {
      return this._delegate.toString();
    }
    /**
     * @returns A reference to the object obtained by
     * appending childPath, removing any duplicate, beginning, or trailing
     * slashes.
     */
  }, {
    key: "child",
    value: function child(childPath) {
      var reference = _getChild(this._delegate, childPath);
      return new ReferenceCompat(reference, this.storage);
    }
  }, {
    key: "root",
    get: function get() {
      return new ReferenceCompat(this._delegate.root, this.storage);
    }
    /**
     * @returns A reference to the parent of the
     * current object, or null if the current object is the root.
     */
  }, {
    key: "parent",
    get: function get() {
      var reference = this._delegate.parent;
      if (reference == null) {
        return null;
      }
      return new ReferenceCompat(reference, this.storage);
    }
    /**
     * Uploads a blob to this object's location.
     * @param data - The blob to upload.
     * @returns An UploadTask that lets you control and
     * observe the upload.
     */
  }, {
    key: "put",
    value: function put(data, metadata) {
      this._throwIfRoot('put');
      return new UploadTaskCompat(uploadBytesResumable(this._delegate, data, metadata), this);
    }
    /**
     * Uploads a string to this object's location.
     * @param value - The string to upload.
     * @param format - The format of the string to upload.
     * @returns An UploadTask that lets you control and
     * observe the upload.
     */
  }, {
    key: "putString",
    value: function putString(value) {
      var format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : StringFormat.RAW;
      var metadata = arguments.length > 2 ? arguments[2] : undefined;
      this._throwIfRoot('putString');
      var data = _dataFromString(format, value);
      var metadataClone = Object.assign({}, metadata);
      if (metadataClone['contentType'] == null && data.contentType != null) {
        metadataClone['contentType'] = data.contentType;
      }
      return new UploadTaskCompat(new _UploadTask(this._delegate, new _FbsBlob(data.data, true), metadataClone), this);
    }
    /**
     * List all items (files) and prefixes (folders) under this storage reference.
     *
     * This is a helper method for calling list() repeatedly until there are
     * no more results. The default pagination size is 1000.
     *
     * Note: The results may not be consistent if objects are changed while this
     * operation is running.
     *
     * Warning: listAll may potentially consume too many resources if there are
     * too many results.
     *
     * @returns A Promise that resolves with all the items and prefixes under
     *  the current storage reference. `prefixes` contains references to
     *  sub-directories and `items` contains references to objects in this
     *  folder. `nextPageToken` is never returned.
     */
  }, {
    key: "listAll",
    value: function listAll() {
      var _this5 = this;
      return _listAll(this._delegate).then(function (r) {
        return new ListResultCompat(r, _this5.storage);
      });
    }
    /**
     * List items (files) and prefixes (folders) under this storage reference.
     *
     * List API is only available for Firebase Rules Version 2.
     *
     * GCS is a key-blob store. Firebase Storage imposes the semantic of '/'
     * delimited folder structure. Refer to GCS's List API if you want to learn more.
     *
     * To adhere to Firebase Rules's Semantics, Firebase Storage does not
     * support objects whose paths end with "/" or contain two consecutive
     * "/"s. Firebase Storage List API will filter these unsupported objects.
     * list() may fail if there are too many unsupported objects in the bucket.
     *
     * @param options - See ListOptions for details.
     * @returns A Promise that resolves with the items and prefixes.
     * `prefixes` contains references to sub-folders and `items`
     * contains references to objects in this folder. `nextPageToken`
     * can be used to get the rest of the results.
     */
  }, {
    key: "list",
    value: function list(options) {
      var _this6 = this;
      return _list(this._delegate, options || undefined).then(function (r) {
        return new ListResultCompat(r, _this6.storage);
      });
    }
    /**
     * A `Promise` that resolves with the metadata for this object. If this
     * object doesn't exist or metadata cannot be retreived, the promise is
     * rejected.
     */
  }, {
    key: "getMetadata",
    value: function getMetadata() {
      return _getMetadata(this._delegate);
    }
    /**
     * Updates the metadata for this object.
     * @param metadata - The new metadata for the object.
     * Only values that have been explicitly set will be changed. Explicitly
     * setting a value to null will remove the metadata.
     * @returns A `Promise` that resolves
     * with the new metadata for this object.
     * @see firebaseStorage.Reference.prototype.getMetadata
     */
  }, {
    key: "updateMetadata",
    value: function updateMetadata(metadata) {
      return _updateMetadata(this._delegate, metadata);
    }
    /**
     * @returns A `Promise` that resolves with the download
     * URL for this object.
     */
  }, {
    key: "getDownloadURL",
    value: function getDownloadURL() {
      return _getDownloadURL(this._delegate);
    }
    /**
     * Deletes the object at this location.
     * @returns A `Promise` that resolves if the deletion succeeds.
     */
  }, {
    key: "delete",
    value: function _delete() {
      this._throwIfRoot('delete');
      return deleteObject(this._delegate);
    }
  }, {
    key: "_throwIfRoot",
    value: function _throwIfRoot(name) {
      if (this._delegate._location.path === '') {
        throw _invalidRootOperation(name);
      }
    }
  }]);
  return ReferenceCompat;
}();
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A service that provides firebaseStorage.Reference instances.
 * @param opt_url gs:// url to a custom Storage Bucket
 */
var StorageServiceCompat = /*#__PURE__*/function () {
  function StorageServiceCompat(app, _delegate) {
    _classCallCheck(this, StorageServiceCompat);
    this.app = app;
    this._delegate = _delegate;
  }
  _createClass(StorageServiceCompat, [{
    key: "maxOperationRetryTime",
    get: function get() {
      return this._delegate.maxOperationRetryTime;
    }
  }, {
    key: "maxUploadRetryTime",
    get: function get() {
      return this._delegate.maxUploadRetryTime;
    }
    /**
     * Returns a firebaseStorage.Reference for the given path in the default
     * bucket.
     */
  }, {
    key: "ref",
    value: function ref(path) {
      if (isUrl(path)) {
        throw _invalidArgument('ref() expected a child path but got a URL, use refFromURL instead.');
      }
      return new ReferenceCompat(_ref2(this._delegate, path), this);
    }
    /**
     * Returns a firebaseStorage.Reference object for the given absolute URL,
     * which must be a gs:// or http[s]:// URL.
     */
  }, {
    key: "refFromURL",
    value: function refFromURL(url) {
      if (!isUrl(url)) {
        throw _invalidArgument('refFromURL() expected a full URL but got a child path, use ref() instead.');
      }
      try {
        _Location.makeFromUrl(url, this._delegate.host);
      } catch (e) {
        throw _invalidArgument('refFromUrl() expected a valid full URL but got an invalid one.');
      }
      return new ReferenceCompat(_ref2(this._delegate, url), this);
    }
  }, {
    key: "setMaxUploadRetryTime",
    value: function setMaxUploadRetryTime(time) {
      this._delegate.maxUploadRetryTime = time;
    }
  }, {
    key: "setMaxOperationRetryTime",
    value: function setMaxOperationRetryTime(time) {
      this._delegate.maxOperationRetryTime = time;
    }
  }, {
    key: "useEmulator",
    value: function useEmulator(host, port) {
      var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
      connectStorageEmulator(this._delegate, host, port, options);
    }
  }]);
  return StorageServiceCompat;
}();
function isUrl(path) {
  return /^[A-Za-z]+:\/\//.test(path);
}
var name = "@firebase/storage-compat";
var version = "0.3.2";

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Type constant for Firebase Storage.
 */
var STORAGE_TYPE = 'storage-compat';
function factory(container, _ref3) {
  var url = _ref3.instanceIdentifier;
  // Dependencies
  var app = container.getProvider('app-compat').getImmediate();
  var storageExp = container.getProvider('storage').getImmediate({
    identifier: url
  });
  var storageServiceCompat = new StorageServiceCompat(app, storageExp);
  return storageServiceCompat;
}
function registerStorage(instance) {
  var namespaceExports = {
    // no-inline
    TaskState: _TaskState,
    TaskEvent: _TaskEvent,
    StringFormat: StringFormat,
    Storage: StorageServiceCompat,
    Reference: ReferenceCompat
  };
  instance.INTERNAL.registerComponent(new Component(STORAGE_TYPE, factory, "PUBLIC" /* ComponentType.PUBLIC */).setServiceProps(namespaceExports).setMultipleInstances(true));
  instance.registerVersion(name, version);
}
registerStorage(firebase);
export { registerStorage };